import Action from './Action';
import Conflict from './Conflict';
import { ActionType, ResolvedAction } from './Action';
import type ProjectedOccupancy from './ProjectedOccupancy';

export default class ConflictFinder {
  private allConsideredActions: Action[];
  private projectedOccupancy: ProjectedOccupancy;
  private resolvedActions: ResolvedAction[];

  constructor(
    allConsideredActions: Action[],
    projectedOccupancy: ProjectedOccupancy,
    resolvedActions: ResolvedAction[]
  ) {
    this.allConsideredActions = allConsideredActions;
    this.projectedOccupancy = projectedOccupancy;
    this.resolvedActions = resolvedActions;
  }

  findAll(): Conflict[] {
    const movingActions = this.allConsideredActions.filter(a => a.type === ActionType.MOVE && a.details.coords);
    const movesByDest = new Map<string, Action[]>();

    for (const action of movingActions) {
      const destKey = action.details.coords!.key;
      if (!movesByDest.has(destKey)) movesByDest.set(destKey, []);
      movesByDest.get(destKey)!.push(action);
    }

    const conflicts: Conflict[] = [];
    const processedIds = new Set<number>();

    // 1. Find head-on collisions and bonks into PENDING actors
    for (const [destKey, movers] of movesByDest.entries()) {
      if (movers.some(m => processedIds.has(m.id))) continue;

      let participants = [...movers];
      const stationaryOccupant = this.projectedOccupancy.getOccupant(movers[0].details.coords!);

      if (stationaryOccupant) {
        const isOccupantAlsoMoving = movingActions.some(a => a.entity.id === stationaryOccupant.id);

        if (!isOccupantAlsoMoving) {
          if (movers.length === 1 && movers[0].entity.id === stationaryOccupant.id) continue;
          if (this.resolvedActions.some(a => a.entity.id === stationaryOccupant.id)) continue;

          const stationaryAction = this.allConsideredActions.find(a => a.entity.id === stationaryOccupant.id);
          if (stationaryAction) {
            participants.push(stationaryAction);
          }
        }
      }

      if (participants.length > 1) {
        const contestedHex = movers[0].details.coords!;
        conflicts.push(new Conflict(participants, contestedHex));
        participants.forEach(p => processedIds.add(p.id));
      }
    }

    // 2. Find swaps among remaining, unprocessed actions
    for (const action of movingActions) {
      if (processedIds.has(action.id)) continue;

      const destCoords = action.details.coords!;
      const swapper = movingActions.find(p =>
        !processedIds.has(p.id) &&
        p.startPos?.key === destCoords.key &&
        p.details.coords?.key === action.startPos?.key
      );

      if (swapper) {
        const participants = [action, swapper];
        // The "contested hex" is ambiguous in a swap. We just pick one.
        conflicts.push(new Conflict(participants, destCoords));
        participants.forEach(p => processedIds.add(p.id));
      }
    }

    return conflicts;
  }
}
